import { isEmpty } from 'lodash';

import {
    ADD_INTERESTS_TARGETING_SET,
    CLOSE_INTERESTS_TARGETING_EDITOR,
    INTERESTS_TARGETING_CHANGE_ITEM,
    INTERESTS_TARGETING_CHANGE_SET_PERIOD,
    InterestsTargetingEditorAction,
    OPEN_INTERESTS_TARGETING_EDITOR,
    REMOVE_INTERESTS_TARGETING_SET,
    RESET_INTERESTS_TARGETING_EDITOR,
    SELECT_ITEM_INTERESTS_EDITOR
} from '../../actions/targetings/interestsEditor';
import { getInterestsIdsAndTree } from '../../selectors/cryptaSegments/cryptaSegments';
import { getOpenedList } from '../../service/InterestsEditorHelpers/InterestsEditorHelpers';
import { InterestsTargetingEditorState } from '../../typings/audienceTargetings';
import { CryptaInterestTypeWeb } from '../../typings/graphql-grid';
import { InterestsSetType } from '../../typings/interestsEditor';
import { InterestsEditorsState, State } from '../../typings/state';

export const defaultInterestsTargetingSetState: InterestsSetType = {
    period: CryptaInterestTypeWeb.all,
    checkedList: [],
    openedList: []
};

export const defaultInterestsTargetingDisabledSetState: InterestsSetType = {
    period: CryptaInterestTypeWeb.short_term,
    checkedList: [],
    openedList: []
};

export const defaultInterestsTargetingEditorsState: InterestsTargetingEditorState = {
    isOpen: false,
    value: [defaultInterestsTargetingSetState]
};

function updateList(list: string[], id: string): string[] {
    return list.includes(id) ? list.filter((listItemId: string) => listItemId !== id) : [...list, id];
}

export function interestsTargetingEditor(
    state: InterestsEditorsState = {},
    action: InterestsTargetingEditorAction,
    fullState: State
): InterestsEditorsState {
    switch (action.type) {
        case OPEN_INTERESTS_TARGETING_EDITOR: {
            const editor = state[action.id] || {
                ...defaultInterestsTargetingEditorsState
            };

            if (!isEmpty(action.value)) {
                const { interestsIds } = getInterestsIdsAndTree(fullState);

                editor.value = action.value.map((set: InterestsSetType) => {
                    const { checkedList, openedList } = set;

                    return checkedList.length && !openedList.length
                        ? {
                              ...set,
                              openedList: getOpenedList(checkedList, interestsIds)
                          }
                        : set;
                });
            }

            return {
                ...state,
                [action.id]: {
                    ...editor,
                    isOpen: true
                }
            };
        }

        case INTERESTS_TARGETING_CHANGE_ITEM: {
            const editor = state[action.id];

            return {
                ...state,
                [action.id]: {
                    ...editor,
                    value: editor.value.map((set: InterestsSetType, index: number) => {
                        const { checkedList } = set;

                        if (index === action.setIndex) {
                            return {
                                ...set,
                                checkedList: updateList(checkedList, action.itemId)
                            };
                        } else {
                            return set;
                        }
                    })
                }
            };
        }

        case SELECT_ITEM_INTERESTS_EDITOR: {
            const editor = state[action.id];

            return {
                ...state,
                [action.id]: {
                    ...editor,
                    value: editor.value.map((set: InterestsSetType, index: number) => {
                        const { openedList } = set;

                        if (index === action.setIndex) {
                            return {
                                ...set,
                                openedList: updateList(openedList, action.itemId)
                            };
                        } else {
                            return set;
                        }
                    })
                }
            };
        }

        case ADD_INTERESTS_TARGETING_SET: {
            const editor = state[action.id];
            const interestsTargetingSetState = action.isDisabledPeriod
                ? defaultInterestsTargetingDisabledSetState
                : defaultInterestsTargetingSetState;

            return {
                ...state,
                [action.id]: {
                    ...editor,
                    value: [...editor.value, { ...interestsTargetingSetState }]
                }
            };
        }

        case INTERESTS_TARGETING_CHANGE_SET_PERIOD: {
            const editor = state[action.id];

            return {
                ...state,
                [action.id]: {
                    ...editor,
                    value: editor.value.map((set: InterestsSetType, index: number) => {
                        return index === action.setIndex ? { ...set, period: action.value } : set;
                    })
                }
            };
        }

        case REMOVE_INTERESTS_TARGETING_SET: {
            const editor = state[action.id];

            return {
                ...state,
                [action.id]: {
                    ...editor,
                    value: editor.value.filter((set: InterestsSetType, index: number) => index !== action.setIndex)
                }
            };
        }

        case CLOSE_INTERESTS_TARGETING_EDITOR: {
            const editor = state[action.id];

            return {
                ...state,
                [action.id]: {
                    ...editor,
                    isOpen: false
                }
            };
        }

        case RESET_INTERESTS_TARGETING_EDITOR: {
            const newState = { ...state };
            delete newState[action.id];

            return newState;
        }

        default:
            return state;
    }
}
